import { SAFE_ZONE_STRINGS } from './strings';

import type { PlaybackBridge } from '@src/hooks/PlaybackBridge';
import type { AspectRatio } from '@src/types.ts/video';
import type React from 'react';

/** The social network the overlay simulates. */
export type SafeZoneChannel =
  | 'linkedin'
  | 'x'
  | 'tiktok'
  | 'instagram'
  | 'facebook'
  | 'threads'
  | 'youtube';

/**
 * The channel surface (envelope) the overlay simulates. Mirrors the
 * format-type enum from the PLAT-9747 distribution-channel model and the
 * Settings → Distribution Channels surface.
 */
export type SafeZoneFormat =
  | 'feed_post'
  | 'short_form_video'
  | 'long_form_video'
  | 'photo_carousel'
  | 'story'
  | 'article';

/**
 * Compound identifier for a registered overlay: `${channel}.${format}`.
 * E.g. `instagram.short_form_video` for Instagram Reels.
 */
export type SafeZoneKey = `${SafeZoneChannel}.${SafeZoneFormat}`;

/** Dropdown selection: a compound key, or the sentinel `'none'`. */
export type SafeZoneSelection = SafeZoneKey | 'none';

/** Build a compound key from its parts. */
export function makeSafeZoneKey(
  channel: SafeZoneChannel,
  format: SafeZoneFormat,
): SafeZoneKey {
  return `${channel}.${format}`;
}

/**
 * Decompose a `SafeZoneSelection` into its `(channel, format)` parts.
 * Returns `(null, null)` for the `'none'` sentinel.
 *
 * Note: aspect-ratio comparisons elsewhere rely on `AspectRatio` being a
 * **string enum** — the enum values ARE the strings (e.g. `AR_1_1 === '1:1'`).
 * If that ever changes to a numeric enum, code that compares
 * `def.supportedAspectRatios.some(ar => ar === dimensionString)` breaks
 * silently. Keep this type as a string union.
 */
export function splitSafeZoneSelection(selection: SafeZoneSelection): {
  channel: SafeZoneChannel | null;
  format: SafeZoneFormat | null;
} {
  if (selection === 'none') return { channel: null, format: null };
  const [channel, format] = selection.split('.') as [
    SafeZoneChannel,
    SafeZoneFormat,
  ];
  return { channel, format };
}

/**
 * Display labels for channels. Sourced from the user-facing string catalog
 * — edit `SAFE_ZONE_STRINGS.channels` rather than this map.
 */
export const CHANNEL_LABELS: Record<SafeZoneChannel, string> = {
  ...SAFE_ZONE_STRINGS.channels,
};

/** Stable channel order — used by the dropdown grouping. */
export const CHANNEL_ORDER: ReadonlyArray<SafeZoneChannel> = [
  'linkedin',
  'x',
  'tiktok',
  'instagram',
  'facebook',
  'threads',
  'youtube',
];

/**
 * Display labels for formats. Sourced from the user-facing string catalog
 * — edit `SAFE_ZONE_STRINGS.formats` rather than this map.
 */
export const FORMAT_LABELS: Record<SafeZoneFormat, string> = {
  ...SAFE_ZONE_STRINGS.formats,
};

/** Stable format order — used when sorting rows under a channel header. */
export const FORMAT_ORDER: ReadonlyArray<SafeZoneFormat> = [
  'feed_post',
  'short_form_video',
  'long_form_video',
  'photo_carousel',
  'story',
  'article',
];

/** The on-screen rect of the video pixels at the active aspect ratio. */
export interface SafeZoneRect {
  width: number;
  height: number;
}

export interface SafeZoneBrand {
  /** Public-facing display name (e.g. company / showcase title). */
  name: string;
  /** `@handle`-style identifier; falls back to a derivation of the name. */
  handle: string;
  /** Logo image URL; may be empty when no brand asset is available. */
  logoUrl: string;
}

/**
 * Boolean knobs an overlay may declare for creator-driven visual variants
 * (e.g. simulated play controls, captions). The toolbar renders one pill per
 * declared knob; the active state lives on `ClipEditSessionStore`.
 */
export type SafeZoneVariantKey = 'showPlayControls' | 'showCaptions';

/** Stable variant order — used when rendering the pill cluster. */
export const VARIANT_ORDER: ReadonlyArray<SafeZoneVariantKey> = [
  'showPlayControls',
  'showCaptions',
];

/**
 * Variant state passed to overlays. Each declared variant resolves to a
 * boolean; undeclared keys are absent.
 */
export type SafeZoneVariantState = Partial<Record<SafeZoneVariantKey, boolean>>;

export interface SafeZoneProps {
  rect: SafeZoneRect;
  aspectRatio: AspectRatio | string;
  brand: SafeZoneBrand;
  variants: SafeZoneVariantState;
  /**
   * Toggle a variant flag from inside the overlay (e.g. the LinkedIn CC
   * button living inside the simulated play-controls bar). Overlays that
   * expose interactive chrome must opt in by setting `pointer-events: auto`
   * on just that element — the overlay root remains non-interactive.
   */
  onToggleVariant?: (key: SafeZoneVariantKey) => void;
  /**
   * Live playback state + controllers. Optional — overlays render without it
   * (falling back to static placeholder values), but when present the
   * simulated chrome (play button, time readout, scrubber) mirrors the real
   * video.
   */
  playback?: PlaybackBridge;
}

/** Hint content shown on hover over a variant pill. */
export interface SafeZoneVariantHint {
  title: string;
  /**
   * Description body, rendered as bullets. Each entry is one bullet line; a
   * single-bullet hint is just a one-element array.
   */
  description: ReadonlyArray<string>;
  proTips?: ReadonlyArray<string>;
}

export interface SafeZoneDefinition {
  channel: SafeZoneChannel;
  format: SafeZoneFormat;
  key: SafeZoneKey;
  /** Display label for the channel (mirrors `CHANNEL_LABELS[channel]`). */
  channelLabel: string;
  /** Display label for the format (mirrors `FORMAT_LABELS[format]`). */
  formatLabel: string;
  /** Aspect ratios this overlay is designed for. */
  supportedAspectRatios: ReadonlyArray<AspectRatio>;
  /**
   * Boolean knobs the overlay supports. The toolbar renders a pill per entry
   * so the creator can flip variants on/off. Omit when the overlay has no
   * variants — no pills render.
   */
  variants?: ReadonlyArray<SafeZoneVariantKey>;
  /**
   * Hover-hint content for each declared variant pill. The toolbar surfaces
   * `title` + `description` (+ optional pro tips) when the creator hovers
   * the corresponding On/Off pill, so they can learn what the toggle gates
   * without leaving the toolbar.
   */
  variantHints?: Partial<Record<SafeZoneVariantKey, SafeZoneVariantHint>>;
  /**
   * Initial variant flags applied the first time the user selects this
   * overlay in the session. Subsequent toggles override the seeded values.
   * Re-selecting the same overlay later does NOT re-seed — user choices
   * persist within the session.
   */
  defaultVariants?: SafeZoneVariantState;
  /** The chrome component itself. */
  Component: React.FC<SafeZoneProps>;
}
